import React, { useState, useEffect, useCallback } from 'react';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  fetchAssessments,
  createAssessment,
  fetchUnits,
  createUnit,
  addAccessCodes,
  fetchCodeStats,
  updateAssessmentSettings,
  openAssessment,
  closeAssessment,
  deadlineState,
  erasureInfo,
  MIN_RETENTION_DAYS,
  MAX_RETENTION_DAYS,
  DEFAULT_RETENTION_DAYS,
  PsicosocialAssessment,
  UnitCodeStat,
} from '../lib/psicosocial-persistencia';
import { generateCodes, hashCode, formatCode } from '../lib/psicosocial-codigos';
import { buildParticipationUrl } from '../lib/psicosocial-formulario';
import { maxCodesForHeadcount, PSICOSOCIAL_SPARE_CODES_PCT, PSICOSOCIAL_MIN_GROUP_SIZE } from '../lib/psicosocial-calculo';
import QRCode from 'qrcode';
import { palabra, plural } from '../lib/plurales';

// Mismo estilo base que NioshMethodologyPage.tsx / RebaMethodologyPage.tsx.
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const fieldDescStyle: React.CSSProperties = {
  fontSize: 12.5, color: '#94a3b8', margin: '0 0 8px 0', lineHeight: 1.45,
};

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
  padding: '8px 16px', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const secondaryButtonStyle: React.CSSProperties = {
  background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6,
  padding: '8px 16px', fontWeight: 600, cursor: 'pointer',
};

const dangerButtonStyle = (disabled: boolean): React.CSSProperties => ({
  background: '#ef4444', color: '#0f172a', border: 'none', borderRadius: 6,
  padding: '8px 16px', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

// ── Estado de la evaluación (paleta y forma de STATUS_CONFIG / StatusBadge
// de RISSOPage.tsx). "abierta_vencida" NO existe en la base: se deriva de
// status = 'abierta' + deadlineState(dueAt, now) = 'vencida'.

type LifecycleBadge = 'borrador' | 'abierta' | 'abierta_vencida' | 'cerrada';

const LIFECYCLE_CONFIG: Record<LifecycleBadge, { label: string; color: string; bg: string }> = {
  borrador:        { label: 'Borrador',          color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  abierta:         { label: 'Abierta',           color: '#22c55e', bg: 'rgba(34,197,94,0.12)'   },
  abierta_vencida: { label: 'Abierta · vencida', color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
  cerrada:         { label: 'Cerrada',           color: '#3b82f6', bg: 'rgba(59,130,246,0.12)'  },
};

function lifecycleBadge(a: PsicosocialAssessment, now: Date): LifecycleBadge {
  if (a.status === 'abierta' && deadlineState(a.dueAt, now).state === 'vencida') return 'abierta_vencida';
  return a.status;
}

function LifecycleStatusBadge({ badge }: { badge: LifecycleBadge }) {
  const cfg = LIFECYCLE_CONFIG[badge];
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700,
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}40`,
    }}>
      {cfg.label}
    </span>
  );
}

// ── Hora de Ecuador continental (UTC−05:00 fija, sin horario de verano).
// dueAt exige zona horaria explícita, así que la conversión se hace aquí
// con el desfase fijo — nunca con la zona horaria del navegador.

const ECUADOR_OFFSET = '-05:00';
const ECUADOR_OFFSET_MS = 5 * 60 * 60 * 1000;

// "2026-10-01T17:30" (datetime-local, hora de Ecuador) → "2026-10-01T17:30:00-05:00"
function ecuadorLocalToIso(value: string): string | null {
  if (value === '') return null;
  const withSeconds = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
  return `${withSeconds}${ECUADOR_OFFSET}`;
}

// ISO en UTC → valor para <input type="datetime-local"> en hora de Ecuador.
function isoToEcuadorLocal(iso: string | null): string {
  if (iso === null) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms - ECUADOR_OFFSET_MS).toISOString().slice(0, 16);
}

function formatEcuadorDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function AssessmentLifecycleCard({
  assessment,
  hasCodes,
  onChanged,
}: {
  assessment: PsicosocialAssessment;
  // Misma regla que la guarda del servidor al abrir (psychosocial_assessment_guard):
  // basta con al menos un código en toda la evaluación, no en cada unidad.
  // null mientras se cargan las unidades (no se muestra la guía todavía).
  hasCodes: boolean | null;
  onChanged: () => Promise<void>;
}) {
  const isDraft = assessment.status === 'borrador';

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const [dueAtLocal, setDueAtLocal] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [retentionDays, setRetentionDays] = useState(String(DEFAULT_RETENTION_DAYS));

  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [closeResult, setCloseResult] = useState<{ units: number; suppressedUnits: number } | null>(null);

  // Al cambiar de evaluación (o recargarla tras guardar/abrir/cerrar), el
  // formulario vuelve a reflejar lo que está guardado en la base.
  useEffect(() => {
    setDueAtLocal(isoToEcuadorLocal(assessment.dueAt));
    setContactEmail(assessment.contactEmail ?? '');
    setContactPhone(assessment.contactPhone ?? '');
    setRetentionDays(String(assessment.retentionDays));
    setCardError(null);
    setConfirmingClose(false);
  }, [assessment]);

  // El resultado del cierre y los avisos solo aplican a la evaluación en
  // la que se ejecutaron.
  useEffect(() => {
    setCloseResult(null);
    setNotice(null);
  }, [assessment.id]);

  const retentionNum = Number(retentionDays);
  const savedDueLocal = isoToEcuadorLocal(assessment.dueAt);
  const isDirty =
    dueAtLocal !== savedDueLocal ||
    contactEmail.trim() !== (assessment.contactEmail ?? '') ||
    contactPhone.trim() !== (assessment.contactPhone ?? '') ||
    retentionNum !== assessment.retentionDays;

  const handleSave = async () => {
    setCardError(null);
    setNotice(null);
    if (!Number.isInteger(retentionNum) || retentionNum < MIN_RETENTION_DAYS || retentionNum > MAX_RETENTION_DAYS) {
      setCardError(`Los días de conservación deben ser un número entero entre ${MIN_RETENTION_DAYS} y ${MAX_RETENTION_DAYS}.`);
      return;
    }
    if (!isDraft && retentionNum > assessment.retentionDays) {
      setCardError(
        `Una vez abierta la evaluación, el plazo de conservación solo se puede acortar ` +
        `(actual: ${assessment.retentionDays} días). No se puede subir a ${retentionNum}.`
      );
      return;
    }
    setSaving(true);
    try {
      // Fuera de borrador, fecha límite y contacto se reenvían EXACTAMENTE
      // como están guardados (el trigger rechaza cualquier diferencia).
      await updateAssessmentSettings({
        assessmentId: assessment.id,
        dueAt: isDraft ? ecuadorLocalToIso(dueAtLocal) : assessment.dueAt,
        retentionDays: retentionNum,
        contactEmail: isDraft ? contactEmail : assessment.contactEmail,
        contactPhone: isDraft ? contactPhone : assessment.contactPhone,
      });
      setNotice('Configuración guardada.');
      await onChanged();
    } catch (e: any) {
      setCardError(e.message ?? 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleOpen = async () => {
    setCardError(null);
    setNotice(null);
    setOpening(true);
    try {
      await openAssessment(assessment.id);
      setNotice('Evaluación abierta: los trabajadores ya pueden responder con sus códigos.');
      await onChanged();
    } catch (e: any) {
      // Mensaje del trigger tal cual (códigos, fecha límite, contacto).
      setCardError(e.message ?? String(e));
    } finally {
      setOpening(false);
    }
  };

  const handleClose = async () => {
    setCardError(null);
    setNotice(null);
    setClosing(true);
    try {
      const result = await closeAssessment(assessment.id);
      setCloseResult(result);
      setConfirmingClose(false);
      await onChanged();
    } catch (e: any) {
      setCardError(e.message ?? String(e));
    } finally {
      setClosing(false);
    }
  };

  const badge = lifecycleBadge(assessment, now);
  const erasure = assessment.status === 'cerrada' ? erasureInfo(assessment.closedAt, assessment.retentionDays, now) : null;
  const readOnlyInput: React.CSSProperties = { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' };

  // Motivos por los que abrir fallaría en el servidor, avisados antes de pulsar.
  const needsCodes = isDraft && hasCodes === false;
  const savedDueExpired = isDraft && assessment.dueAt !== null && deadlineState(assessment.dueAt, now).state === 'vencida';
  const openBlockedReason =
    isDirty ? 'Guarda los cambios antes de abrir la evaluación'
    : needsCodes ? 'Genera los códigos de al menos una unidad antes de abrir la evaluación'
    : savedDueExpired ? 'La fecha límite guardada ya pasó: guarda una fecha futura antes de abrir'
    : null;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', margin: 0 }}>Configuración de la evaluación</h2>
        <LifecycleStatusBadge badge={badge} />
      </div>

      {assessment.openedAt && (
        <p style={fieldDescStyle}>Abierta el {formatEcuadorDateTime(assessment.openedAt)}.</p>
      )}
      {badge === 'abierta_vencida' && (
        <p style={{ ...fieldDescStyle, color: '#fca5a5' }}>
          La fecha límite ya pasó: el cuestionario no acepta más respuestas. Cierra la evaluación para calcular los resultados.
        </p>
      )}
      {!isDraft && (
        <p style={fieldDescStyle}>
          Fecha límite y contacto ya no se pueden cambiar. El plazo de conservación solo se puede acortar.
        </p>
      )}
      {needsCodes && (
        <div style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid #3b82f640', color: '#bfdbfe', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, lineHeight: 1.5 }}>
          <strong>Orden de pasos para abrir la evaluación:</strong>
          <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
            <li>Crea las unidades y genera sus códigos (tarjeta "Nueva unidad", más abajo).</li>
            <li>Guarda esta configuración con una fecha límite futura y los datos de contacto.</li>
            <li>Pulsa "Abrir evaluación".</li>
          </ol>
        </div>
      )}
      {savedDueExpired && (
        <p style={{ ...fieldDescStyle, color: '#fca5a5' }}>
          La fecha límite guardada ({formatEcuadorDateTime(assessment.dueAt as string)}) ya pasó. Cámbiala por una
          fecha futura y guarda la configuración antes de abrir la evaluación.
        </p>
      )}

      {cardError && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          {cardError}
        </div>
      )}
      {notice && (
        <div style={{ background: 'rgba(34,197,94,0.12)', color: '#86efac', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          {notice}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Fecha límite (hora de Ecuador, UTC−5)</label>
          <input
            style={isDraft ? inputStyle : readOnlyInput}
            type="datetime-local"
            value={dueAtLocal}
            disabled={!isDraft}
            onChange={e => setDueAtLocal(e.target.value)}
          />
        </div>
        <div>
          <label style={labelStyle}>Correo de contacto</label>
          <input
            style={isDraft ? inputStyle : readOnlyInput}
            type="email"
            value={contactEmail}
            disabled={!isDraft}
            onChange={e => setContactEmail(e.target.value)}
            placeholder="sso@empresa.com"
          />
        </div>
        <div>
          <label style={labelStyle}>Teléfono de contacto</label>
          <input
            style={isDraft ? inputStyle : readOnlyInput}
            type="tel"
            value={contactPhone}
            disabled={!isDraft}
            onChange={e => setContactPhone(e.target.value)}
            placeholder="0991234567"
          />
        </div>
        <div>
          <label style={labelStyle}>Días de conservación de respuestas</label>
          <input
            style={inputStyle}
            type="number"
            min={MIN_RETENTION_DAYS}
            max={isDraft ? MAX_RETENTION_DAYS : assessment.retentionDays}
            value={retentionDays}
            onChange={e => setRetentionDays(e.target.value)}
          />
        </div>
      </div>
      <p style={fieldDescStyle}>
        Las respuestas individuales se borran automáticamente {MIN_RETENTION_DAYS}–{MAX_RETENTION_DAYS} días después
        del cierre (por defecto {DEFAULT_RETENTION_DAYS}); los resultados agregados se conservan.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button onClick={handleSave} disabled={saving || !isDirty} style={primaryButtonStyle(saving || !isDirty)}>
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </button>

        {isDraft && (
          <button
            onClick={handleOpen}
            disabled={opening || openBlockedReason !== null}
            title={openBlockedReason ?? undefined}
            style={{ ...primaryButtonStyle(opening || openBlockedReason !== null), background: '#22c55e' }}
          >
            {opening ? 'Abriendo...' : 'Abrir evaluación'}
          </button>
        )}
        {isDraft && openBlockedReason && (
          <span style={{ fontSize: 12.5, color: '#94a3b8' }}>{openBlockedReason}.</span>
        )}

        {assessment.status === 'abierta' && !confirmingClose && (
          <button onClick={() => { setCardError(null); setConfirmingClose(true); }} style={dangerButtonStyle(false)}>
            Cerrar evaluación
          </button>
        )}
      </div>

      {assessment.status === 'abierta' && confirmingClose && (
        <div style={{ border: '1px solid #ef4444', borderRadius: 8, padding: 14, marginTop: 14, background: 'rgba(239,68,68,0.08)' }}>
          <p style={{ color: '#fecaca', fontSize: 13, margin: '0 0 10px 0', lineHeight: 1.45 }}>
            <strong>Esta acción es irreversible.</strong> Al cerrar, nadie más podrá responder (aunque tenga un código
            sin usar), se calculan los resultados agregados por unidad y empieza a correr el plazo de conservación de{' '}
            {assessment.retentionDays} días para borrar las respuestas individuales.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleClose} disabled={closing} style={dangerButtonStyle(closing)}>
              {closing ? 'Cerrando...' : 'Sí, cerrar definitivamente'}
            </button>
            <button onClick={() => setConfirmingClose(false)} disabled={closing} style={secondaryButtonStyle}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {closeResult && (
        <div style={{ background: 'rgba(59,130,246,0.12)', color: '#bfdbfe', padding: '10px 14px', borderRadius: 8, marginTop: 14, fontSize: 13, lineHeight: 1.45 }}>
          Evaluación cerrada. Se procesaron {plural(closeResult.units, 'unidad', 'unidades')}:{' '}
          {closeResult.units - closeResult.suppressedUnits} con resultados agregados y {closeResult.suppressedUnits}{' '}
          {palabra(closeResult.suppressedUnits, 'suprimida', 'suprimidas')} por no alcanzar el grupo mínimo de {PSICOSOCIAL_MIN_GROUP_SIZE} respuestas (para proteger el anonimato).
        </div>
      )}

      {assessment.status === 'cerrada' && (
        <div style={{ background: '#1e293b', color: '#cbd5e1', padding: '10px 14px', borderRadius: 8, marginTop: 14, fontSize: 13, lineHeight: 1.45 }}>
          {assessment.responsesPurgedAt ? (
            <>Las respuestas individuales ya se borraron el {formatEcuadorDateTime(assessment.responsesPurgedAt)}. Solo se conservan los resultados agregados.</>
          ) : erasure ? (
            <>
              Cerrada el {formatEcuadorDateTime(assessment.closedAt as string)}. Las respuestas individuales se borrarán el{' '}
              <strong>{formatEcuadorDateTime(erasure.erasureAt)}</strong>{' '}
              ({erasure.daysLeft === 0 ? 'en la próxima purga automática' : `${erasure.daysLeft === 1 ? 'falta' : 'faltan'} ${plural(erasure.daysLeft, 'día', 'días')}`}).
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function PsicosocialCodigosPage() {
  const { company } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const [assessments, setAssessments] = useState<PsicosocialAssessment[]>([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [newAssessmentTitle, setNewAssessmentTitle] = useState('');
  const [creatingAssessment, setCreatingAssessment] = useState(false);

  const [unitStats, setUnitStats] = useState<UnitCodeStat[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const [unitName, setUnitName] = useState('');
  const [headcount, setHeadcount] = useState('');
  const [extraCodes, setExtraCodes] = useState('0');
  const [creatingUnit, setCreatingUnit] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Códigos en texto plano recién generados — SOLO en memoria de React,
  // nunca en localStorage/sessionStorage ni en consola. Se pierden al
  // recargar la página o al cerrar este aviso; no hay forma de volver
  // a verlos después (el servidor nunca guarda el código en claro).
  const [generatedCodes, setGeneratedCodes] = useState<string[] | null>(null);
  const [generatedForUnitName, setGeneratedForUnitName] = useState('');

  // QR por código — igual que generatedCodes, SOLO en memoria de React,
  // nunca persistido. Se recalcula cada vez que generatedCodes cambia.
  const [qrCards, setQrCards] = useState<{ code: string; qrDataUrl: string }[]>([]);

  const loadAssessments = useCallback(async () => {
    if (!companyId) { setLoadingAssessments(false); return; }
    setLoadingAssessments(true);
    try {
      const rows = await fetchAssessments(companyId);
      setAssessments(rows);
      if (rows.length > 0) {
        setSelectedAssessmentId(prev => (prev && rows.some(r => r.id === prev)) ? prev : rows[0].id);
      }
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar las evaluaciones');
    } finally {
      setLoadingAssessments(false);
    }
  }, [companyId]);

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  const loadUnitStats = useCallback(async (assessmentId: string) => {
    if (!assessmentId) { setUnitStats([]); return; }
    setLoadingUnits(true);
    try {
      const stats = await fetchCodeStats(assessmentId);
      setUnitStats(stats);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar las unidades');
    } finally {
      setLoadingUnits(false);
    }
  }, []);

  useEffect(() => { loadUnitStats(selectedAssessmentId); }, [selectedAssessmentId, loadUnitStats]);

  // Genera un QR por código recién creado — cada QR codifica la misma URL
  // de participación que ya usa el trabajador (buildParticipationUrl),
  // código incluido en el fragmento (#c=...), nunca en el query string.
  useEffect(() => {
    let cancelled = false;
    setQrCards([]);
    if (!generatedCodes || !selectedAssessmentId) return;

    (async () => {
      try {
        const cards = await Promise.all(
          generatedCodes.map(async code => {
            const url = buildParticipationUrl(window.location.origin, selectedAssessmentId, code);
            const qrDataUrl = await QRCode.toDataURL(url);
            return { code, qrDataUrl };
          })
        );
        if (!cancelled) setQrCards(cards);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Error al generar los códigos QR');
      }
    })();

    return () => { cancelled = true; };
  }, [generatedCodes, selectedAssessmentId]);

  const handleCreateAssessment = async () => {
    setError(null);
    if (!newAssessmentTitle.trim()) { setError('Ingresa un título para la evaluación'); return; }
    setCreatingAssessment(true);
    try {
      const id = await createAssessment({ companyId, title: newAssessmentTitle });
      setNewAssessmentTitle('');
      await loadAssessments();
      setSelectedAssessmentId(id);
    } catch (e: any) {
      setError(e.message ?? 'Error al crear la evaluación');
    } finally {
      setCreatingAssessment(false);
    }
  };

  const headcountNum = parseInt(headcount, 10);
  const extraCodesNum = parseInt(extraCodes || '0', 10);
  const headcountValido = Number.isInteger(headcountNum) && headcountNum >= 1;
  // Máximo de repuestos para una unidad nueva (sin códigos previos): mismo
  // tope que psychosocial_max_codes en el servidor.
  const maxRepuestos = headcountValido ? maxCodesForHeadcount(headcountNum) - headcountNum : null;
  const repuestosExcedidos = maxRepuestos !== null && Number.isInteger(extraCodesNum) && extraCodesNum > maxRepuestos;
  const puedeCrearUnidad =
    unitName.trim().length > 0 &&
    headcountValido &&
    Number.isInteger(extraCodesNum) && extraCodesNum >= 0 &&
    !repuestosExcedidos;

  const handleCreateUnit = async () => {
    setError(null);
    if (!selectedAssessmentId) { setError('Selecciona o crea una evaluación primero'); return; }
    if (repuestosExcedidos) {
      setError(
        `Con ${plural(headcountNum, 'trabajador', 'trabajadores')} puedes generar como máximo ${plural(maxRepuestos, 'código', 'códigos')} de repuesto ` +
        `(${PSICOSOCIAL_SPARE_CODES_PCT}% de los trabajadores, redondeado hacia arriba).`
      );
      return;
    }
    if (!puedeCrearUnidad) { setError('Revisa el nombre, el número de trabajadores y los códigos de repuesto'); return; }

    const trimmedName = unitName.trim();
    const totalCodes = headcountNum + extraCodesNum;

    setCreatingUnit(true);
    try {
      await createUnit({ assessmentId: selectedAssessmentId, name: trimmedName, headcount: headcountNum });

      // createUnit() no retorna el id del registro insertado (mismo patrón
      // que createNioshAssessment()) — se vuelve a consultar y se ubica por
      // nombre, único por evaluación (UNIQUE (assessment_id, name) en la
      // tabla), para tener el unitId real antes de generar los códigos.
      const refreshedUnits = await fetchUnits(selectedAssessmentId);
      const created = refreshedUnits.find(u => u.name === trimmedName);
      if (!created) {
        throw new Error('La unidad se creó, pero no se pudo ubicar para generarle códigos. Recarga la página e inténtalo de nuevo.');
      }

      let plainCodes: string[];
      let hashes: string[];
      try {
        plainCodes = generateCodes(totalCodes);
        hashes = await Promise.all(plainCodes.map(code => hashCode(code)));
        await addAccessCodes(created.id, hashes);
      } catch (codeError: any) {
        // La unidad ya quedó creada aunque esto falle (p. ej. si el total
        // excede el tope del servidor, psychosocial_max_codes). El cliente
        // ya valida el mismo tope antes de enviar (maxCodesForHeadcount); el
        // mensaje del servidor se muestra tal cual si aun así falla.
        throw new Error(
          `La unidad "${trimmedName}" se creó, pero no se pudieron generar sus códigos: ${codeError.message ?? codeError}`
        );
      }

      setGeneratedCodes(plainCodes);
      setGeneratedForUnitName(trimmedName);
      setUnitName('');
      setHeadcount('');
      setExtraCodes('0');
      await loadUnitStats(selectedAssessmentId);
    } catch (e: any) {
      setError(e.message ?? 'Error al crear la unidad');
    } finally {
      setCreatingUnit(false);
    }
  };

  const selectedAssessment = assessments.find(a => a.id === selectedAssessmentId) ?? null;

  if (loadingAssessments) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      {/* Al imprimir: oculta todo lo demás (dashboard, formularios, texto
          plano) y muestra solo la cuadrícula de tarjetas QR. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .psico-print-only, .psico-print-only * { visibility: visible; }
          .psico-print-only { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>

      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Cuestionario Psicosocial — Códigos de acceso</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Genera unidades y códigos de acceso anónimos para que los trabajadores respondan el cuestionario de riesgo psicosocial.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ===== Selección / creación de evaluación ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Evaluación</h2>

        {assessments.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Evaluación activa</label>
            <select style={inputStyle} value={selectedAssessmentId} onChange={e => setSelectedAssessmentId(e.target.value)}>
              {assessments.map(a => (
                <option key={a.id} value={a.id} style={{ color: '#000' }}>
                  {a.title} ({a.status})
                </option>
              ))}
            </select>
          </div>
        )}

        <label style={labelStyle}>{assessments.length > 0 ? 'Crear otra evaluación' : 'Título de la evaluación'}</label>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            style={{ ...inputStyle, marginTop: 0 }}
            type="text"
            value={newAssessmentTitle}
            onChange={e => setNewAssessmentTitle(e.target.value)}
            placeholder="Ej. Evaluación de riesgo psicosocial 2026"
          />
          <button
            onClick={handleCreateAssessment}
            disabled={creatingAssessment || !newAssessmentTitle.trim()}
            style={{ ...primaryButtonStyle(creatingAssessment || !newAssessmentTitle.trim()), whiteSpace: 'nowrap' }}
          >
            {creatingAssessment ? 'Creando...' : 'Crear evaluación'}
          </button>
        </div>
      </div>

      {selectedAssessmentId && (
        <>
          {/* ===== Configuración / ciclo de vida ===== */}
          {selectedAssessment && (
            <AssessmentLifecycleCard
              assessment={selectedAssessment}
              hasCodes={loadingUnits ? null : unitStats.some(s => s.codesTotal > 0)}
              onChanged={loadAssessments}
            />
          )}

          {/* ===== Nueva unidad (solo en borrador: la política psy_units_insert
              rechaza crear unidades en cualquier otro estado) ===== */}
          {selectedAssessment?.status === 'borrador' ? (
          <div style={cardStyle}>
            <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 4 }}>Nueva unidad</h2>
            <p style={fieldDescStyle}>
              Se generan por defecto exactamente los códigos que indiques en "Nº de trabajadores". Los códigos de
              repuesto son opcionales, para reponer un código extraviado o mal entregado — el servidor rechaza el
              total si excede el tope permitido (trabajadores + {PSICOSOCIAL_SPARE_CODES_PCT}% de repuestos, redondeado
              hacia arriba).
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Nombre de la unidad</label>
                <input
                  style={inputStyle}
                  type="text"
                  value={unitName}
                  onChange={e => setUnitName(e.target.value)}
                  placeholder="Ej. Bodega Norte"
                />
              </div>
              <div>
                <label style={labelStyle}>Nº de trabajadores</label>
                <input
                  style={inputStyle}
                  type="number"
                  min={1}
                  value={headcount}
                  onChange={e => setHeadcount(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Códigos de repuesto (opcional)</label>
                <input
                  style={inputStyle}
                  type="number"
                  min={0}
                  max={maxRepuestos ?? undefined}
                  value={extraCodes}
                  onChange={e => setExtraCodes(e.target.value)}
                />
                <p style={{ fontSize: 12, margin: '4px 0 0 0', color: repuestosExcedidos ? '#fca5a5' : '#94a3b8' }}>
                  {maxRepuestos === null
                    ? 'Máximo: indica primero el Nº de trabajadores'
                    : `Máximo: ${maxRepuestos}${repuestosExcedidos ? ' — reduce la cantidad para continuar' : ''}`}
                </p>
              </div>
            </div>
            <button
              onClick={handleCreateUnit}
              disabled={creatingUnit || !puedeCrearUnidad}
              style={primaryButtonStyle(creatingUnit || !puedeCrearUnidad)}
            >
              {creatingUnit ? 'Creando unidad y generando códigos...' : 'Crear unidad y generar códigos'}
            </button>
          </div>
          ) : (
          <div style={cardStyle}>
            <p style={{ ...fieldDescStyle, margin: 0 }}>
              Las unidades solo se pueden crear mientras la evaluación está en borrador.
            </p>
          </div>
          )}

          {/* ===== Códigos recién generados (una sola vez) ===== */}
          {generatedCodes && (
            <div style={{ ...cardStyle, border: '1px solid #2FBF71' }}>
              <h2 style={{ fontSize: 14, color: '#2FBF71', marginTop: 0, marginBottom: 4 }}>
                Códigos de "{generatedForUnitName}" — cópialos o imprímelos ahora
              </h2>
              <p style={fieldDescStyle}>
                Estos códigos solo se muestran esta vez. El servidor nunca guarda el código en texto plano — únicamente
                su huella (hash) — así que si cierras este aviso sin copiarlos, no hay forma de recuperarlos: habría
                que generar códigos de repuesto nuevos.
              </p>
              <textarea
                readOnly
                value={generatedCodes.map(formatCode).join('\n')}
                onClick={e => (e.target as HTMLTextAreaElement).select()}
                style={{
                  width: '100%', minHeight: 160, background: '#0f172a', border: '1px solid #334155',
                  borderRadius: 6, color: '#e2e8f0', padding: 10, fontFamily: 'monospace', fontSize: 13,
                  boxSizing: 'border-box', resize: 'vertical',
                }}
              />
              {qrCards.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 12 }}>Generando códigos QR…</p>
              ) : (
                <div
                  className="psico-print-only"
                  style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 16, marginTop: 16,
                  }}
                >
                  {qrCards.map(({ code, qrDataUrl }) => (
                    <div
                      key={code}
                      style={{
                        border: '1px solid #cbd5e1', borderRadius: 8, padding: 16, textAlign: 'center',
                        background: '#fff', color: '#0f172a', pageBreakInside: 'avoid',
                      }}
                    >
                      <img src={qrDataUrl} alt="Código QR de acceso al cuestionario" style={{ width: 160, height: 160 }} />
                      <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1, marginTop: 8, fontFamily: 'monospace' }}>
                        {formatCode(code)}
                      </div>
                      <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{generatedForUnitName}</div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => window.print()}
                disabled={qrCards.length === 0}
                style={{
                  background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
                  padding: '8px 16px', fontWeight: 600,
                  cursor: qrCards.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: qrCards.length === 0 ? 0.6 : 1,
                  marginTop: 16, marginRight: 10,
                }}
              >
                Imprimir hoja
              </button>
              <button
                onClick={() => { setGeneratedCodes(null); setGeneratedForUnitName(''); setQrCards([]); }}
                style={{
                  background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6,
                  padding: '8px 16px', fontWeight: 600, cursor: 'pointer', marginTop: 16,
                }}
              >
                Ya los copié — cerrar
              </button>
            </div>
          )}

          {/* ===== Unidades existentes (agregado, sin códigos individuales) ===== */}
          <div style={cardStyle}>
            <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Unidades de esta evaluación</h2>
            {loadingUnits ? (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>Cargando…</p>
            ) : unitStats.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>Todavía no hay unidades creadas para esta evaluación.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>Unidad</th>
                    <th style={{ padding: '6px 8px' }}>Trabajadores</th>
                    <th style={{ padding: '6px 8px' }}>Códigos generados</th>
                    <th style={{ padding: '6px 8px' }}>Códigos usados</th>
                  </tr>
                </thead>
                <tbody>
                  {unitStats.map(s => (
                    <tr key={s.unitId} style={{ borderBottom: '1px solid #1e293b' }}>
                      <td style={{ padding: '6px 8px' }}>{s.unitName}</td>
                      <td style={{ padding: '6px 8px' }}>{s.headcount}</td>
                      {/* Tras la purga los códigos ya no existen: code_stats daría 0/0. */}
                      <td style={{ padding: '6px 8px' }}>{selectedAssessment?.responsesPurgedAt ? '—' : s.codesTotal}</td>
                      <td style={{ padding: '6px 8px' }}>{selectedAssessment?.responsesPurgedAt ? '—' : s.codesUsed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {selectedAssessment?.responsesPurgedAt && unitStats.length > 0 && (
              <p style={{ ...fieldDescStyle, marginTop: 10, marginBottom: 0 }}>
                Los códigos de acceso se borraron junto con las respuestas individuales el{' '}
                {formatEcuadorDateTime(selectedAssessment.responsesPurgedAt)}. La participación final quedó registrada
                en los resultados agregados.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
